import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

// Prefix for this module based on folder structure and file name
class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scriptsSupplier.settings.view.SettingsReportsView.';

  public static readonly caption = `${Id.prefix}caption`;
  public static readonly saved = `${Id.prefix}saved`;
  public static readonly heading = `${Id.prefix}heading`;
  public static readonly introduction = `${Id.prefix}introduction`;
  public static readonly columnReport = `${Id.prefix}columnReport`;
  public static readonly columnAvailable = `${Id.prefix}columnAvailable`;
  public static readonly columnStoredOnIssue = `${Id.prefix}columnStoredOnIssue`;
  public static readonly storedOnIssueTooltip = `${Id.prefix}storedOnIssueTooltip`;
  public static readonly internalTag = `${Id.prefix}internalTag`;
  public static readonly changedTag = `${Id.prefix}changedTag`;
  public static readonly optionsSummary = `${Id.prefix}optionsSummary`;
  public static readonly optionsHeading = `${Id.prefix}optionsHeading`;
  public static readonly optionsIntroduction = `${Id.prefix}optionsIntroduction`;
  public static readonly sectionSections = `${Id.prefix}sectionSections`;
  public static readonly sectionPricing = `${Id.prefix}sectionPricing`;
  public static readonly sectionFooter = `${Id.prefix}sectionFooter`;
  public static readonly lockedTooltip = `${Id.prefix}lockedTooltip`;
  public static readonly resetToDefaults = `${Id.prefix}resetToDefaults`;
  public static readonly preview = `${Id.prefix}preview`;
  public static readonly noneAvailable = `${Id.prefix}noneAvailable`;

  // Report names (the same wording as the dealer's Reports menu)
  public static readonly reportA = `${Id.prefix}reportA`;
  public static readonly reportB = `${Id.prefix}reportB`;
  public static readonly reportC = `${Id.prefix}reportC`;
  public static readonly reportG = `${Id.prefix}reportG`;
  public static readonly reportJ = `${Id.prefix}reportJ`;
  public static readonly reportE = `${Id.prefix}reportE`;
  public static readonly reportH = `${Id.prefix}reportH`;

  // Options
  public static readonly coverPage = `${Id.prefix}coverPage`;
  public static readonly infoPage = `${Id.prefix}infoPage`;
  public static readonly globalSpecifications = `${Id.prefix}globalSpecifications`;
  public static readonly itemSummary = `${Id.prefix}itemSummary`;
  public static readonly quoteCalculations = `${Id.prefix}quoteCalculations`;
  public static readonly itemDetails = `${Id.prefix}itemDetails`;
  public static readonly annotations = `${Id.prefix}annotations`;
  public static readonly dealerTerms = `${Id.prefix}dealerTerms`;
  public static readonly supplierTerms = `${Id.prefix}supplierTerms`;
  public static readonly lineItemPrice = `${Id.prefix}lineItemPrice`;
  public static readonly groupTotals = `${Id.prefix}groupTotals`;
  public static readonly priceSummary = `${Id.prefix}priceSummary`;
  public static readonly priceSummaryNone = `${Id.prefix}priceSummaryNone`;
  public static readonly priceSummarySummary = `${Id.prefix}priceSummarySummary`;
  public static readonly priceSummaryDealerCost = `${Id.prefix}priceSummaryDealerCost`;
  public static readonly itemSummaryLayout = `${Id.prefix}itemSummaryLayout`;
  public static readonly itemSummaryLayoutCompact = `${Id.prefix}itemSummaryLayoutCompact`;
  public static readonly itemSummaryLayoutDetailed = `${Id.prefix}itemSummaryLayoutDetailed`;
  public static readonly infoPageLayout = `${Id.prefix}infoPageLayout`;
  public static readonly infoPageLayoutStandard = `${Id.prefix}infoPageLayoutStandard`;
  public static readonly infoPageLayoutGrid = `${Id.prefix}infoPageLayoutGrid`;
  public static readonly map = `${Id.prefix}map`;
  public static readonly sectionFlow = `${Id.prefix}sectionFlow`;
  public static readonly sectionFlowPageBreak = `${Id.prefix}sectionFlowPageBreak`;
  public static readonly sectionFlowContinuous = `${Id.prefix}sectionFlowContinuous`;
  public static readonly moveUp = `${Id.prefix}moveUp`;
  public static readonly moveDown = `${Id.prefix}moveDown`;
  public static readonly dragToReorder = `${Id.prefix}dragToReorder`;
  public static readonly resetOrder = `${Id.prefix}resetOrder`;
  public static readonly pages = `${Id.prefix}pages`;
  public static readonly dimensions = `${Id.prefix}dimensions`;
  public static readonly manufacturerNotes = `${Id.prefix}manufacturerNotes`;
  public static readonly initialsPanel = `${Id.prefix}initialsPanel`;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class LangSettingsReportsView {
  private constructor() {}

  public static caption = (): string => interpolate(Id.caption);
  public static saved = (): string => interpolate(Id.saved);
  public static heading = (): string => interpolate(Id.heading);
  public static introduction = (): string => interpolate(Id.introduction);
  public static columnReport = (): string => interpolate(Id.columnReport);
  public static columnAvailable = (): string => interpolate(Id.columnAvailable);
  public static columnStoredOnIssue = (): string => interpolate(Id.columnStoredOnIssue);
  public static storedOnIssueTooltip = (): string => interpolate(Id.storedOnIssueTooltip);
  public static internalTag = (): string => interpolate(Id.internalTag);
  public static changedTag = (): string => interpolate(Id.changedTag);
  public static optionsSummary = (params: { report: string }): string => interpolate(Id.optionsSummary, params);
  public static optionsHeading = (): string => interpolate(Id.optionsHeading);
  public static optionsIntroduction = (): string => interpolate(Id.optionsIntroduction);
  public static sectionSections = (): string => interpolate(Id.sectionSections);
  public static sectionPricing = (): string => interpolate(Id.sectionPricing);
  public static sectionFooter = (): string => interpolate(Id.sectionFooter);
  public static lockedTooltip = (): string => interpolate(Id.lockedTooltip);
  public static resetToDefaults = (): string => interpolate(Id.resetToDefaults);
  public static preview = (): string => interpolate(Id.preview);
  public static noneAvailable = (): string => interpolate(Id.noneAvailable);

  public static reportA = (): string => interpolate(Id.reportA);
  public static reportB = (): string => interpolate(Id.reportB);
  public static reportC = (): string => interpolate(Id.reportC);
  public static reportG = (): string => interpolate(Id.reportG);
  public static reportJ = (): string => interpolate(Id.reportJ);
  public static reportE = (): string => interpolate(Id.reportE);
  public static reportH = (): string => interpolate(Id.reportH);

  public static coverPage = (): string => interpolate(Id.coverPage);
  public static infoPage = (): string => interpolate(Id.infoPage);
  public static globalSpecifications = (): string => interpolate(Id.globalSpecifications);
  public static itemSummary = (): string => interpolate(Id.itemSummary);
  public static quoteCalculations = (): string => interpolate(Id.quoteCalculations);
  public static itemDetails = (): string => interpolate(Id.itemDetails);
  public static annotations = (): string => interpolate(Id.annotations);
  public static dealerTerms = (): string => interpolate(Id.dealerTerms);
  public static supplierTerms = (): string => interpolate(Id.supplierTerms);
  public static lineItemPrice = (): string => interpolate(Id.lineItemPrice);
  public static groupTotals = (): string => interpolate(Id.groupTotals);
  public static priceSummary = (): string => interpolate(Id.priceSummary);
  public static priceSummaryNone = (): string => interpolate(Id.priceSummaryNone);
  public static priceSummarySummary = (): string => interpolate(Id.priceSummarySummary);
  public static priceSummaryDealerCost = (): string => interpolate(Id.priceSummaryDealerCost);
  public static itemSummaryLayout = (): string => interpolate(Id.itemSummaryLayout);
  public static itemSummaryLayoutCompact = (): string => interpolate(Id.itemSummaryLayoutCompact);
  public static itemSummaryLayoutDetailed = (): string => interpolate(Id.itemSummaryLayoutDetailed);
  public static infoPageLayout = (): string => interpolate(Id.infoPageLayout);
  public static infoPageLayoutStandard = (): string => interpolate(Id.infoPageLayoutStandard);
  public static infoPageLayoutGrid = (): string => interpolate(Id.infoPageLayoutGrid);
  public static map = (): string => interpolate(Id.map);
  public static sectionFlow = (): string => interpolate(Id.sectionFlow);
  public static sectionFlowPageBreak = (): string => interpolate(Id.sectionFlowPageBreak);
  public static sectionFlowContinuous = (): string => interpolate(Id.sectionFlowContinuous);
  public static moveUp = (params: { section: string }): string => interpolate(Id.moveUp, params);
  public static moveDown = (params: { section: string }): string => interpolate(Id.moveDown, params);
  public static dragToReorder = (): string => interpolate(Id.dragToReorder);
  public static resetOrder = (): string => interpolate(Id.resetOrder);
  public static pages = (): string => interpolate(Id.pages);
  public static dimensions = (): string => interpolate(Id.dimensions);
  public static manufacturerNotes = (): string => interpolate(Id.manufacturerNotes);
  public static initialsPanel = (): string => interpolate(Id.initialsPanel);
}

export class SettingsReportsViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.caption, 'Reports', 'Caption of the Reports settings tab');
    register.add(Id.saved, 'Report settings saved', 'Toast after the report settings are saved');
    register.add(Id.heading, "{{glossary 'quote'}} Reports", 'Heading of the Reports settings tab');
    register.add(
      Id.introduction,
      "Choose which reports your {{glossary 'franchisee:plural'}} can produce, which are kept when a {{glossary 'quote'}} is issued, and how each one is printed. A change can take up to an hour to reach {{glossary 'franchisee:plural'}}.",
      'Introduction text at the top of the Reports settings tab'
    );
    register.add(Id.columnReport, 'Report', 'Report column header');
    register.add(
      Id.columnAvailable,
      "Available to {{glossary 'franchisee:plural'}}",
      'Column header: the report is offered in the dealer Reports menu'
    );
    register.add(Id.columnStoredOnIssue, 'Kept on issue', 'Column header: the report is stored when a quote is issued');
    register.add(
      Id.storedOnIssueTooltip,
      "Generate and keep this report with every issued {{glossary 'quote'}}. Only possible while the report is available.",
      'Tooltip for the kept on issue column'
    );
    register.add(Id.internalTag, 'Internal', 'Tag marking a report for internal dealer use');
    register.add(Id.changedTag, 'Changed', 'Tag marking a setting changed from its default');
    register.add(Id.optionsSummary, '{{report}} - options', 'Summary of the options panel of one report');
    register.add(Id.optionsHeading, 'Report Options', 'Heading above the per-report options panels');
    register.add(
      Id.optionsIntroduction,
      'Choose what each report prints. Options that define what a report is for are fixed and shown greyed out: for example, line item price cannot be switched on for the No Line Item Price report, and the No Price report never shows a price summary.',
      'Introduction text above the per-report options panels'
    );
    register.add(Id.sectionSections, 'Sections', 'Heading of the report sections options group');
    register.add(Id.sectionPricing, 'Pricing', 'Heading of the pricing options group');
    register.add(Id.sectionFooter, 'Footer', 'Heading of the footer options group');
    register.add(Id.lockedTooltip, 'Fixed for this report', 'Tooltip on an option that cannot be changed');
    register.add(Id.resetToDefaults, 'Reset to defaults', 'Button that returns one report to its default settings');
    register.add(
      Id.preview,
      'Preview',
      'Button that downloads a sample PDF of the report with the options on screen, saved or not'
    );
    register.add(
      Id.noneAvailable,
      "At least one report must stay available to {{glossary 'franchisee:plural'}}",
      'Validation message when every report is switched off'
    );

    register.add(Id.reportA, 'Full Detail', 'Report A name');
    register.add(Id.reportB, 'No Line Item Price', 'Report B name');
    register.add(Id.reportC, 'With Item Price, No Dimensions', 'Report C name');
    register.add(Id.reportG, 'No Price', 'Report G name');
    register.add(Id.reportJ, 'Summary', 'Report J name');
    register.add(Id.reportE, "{{glossary 'supplier'}} Cost, Dealer Internal View", 'Report E name');
    register.add(Id.reportH, 'Dealer Internal View, Multiplier', 'Report H name');

    register.add(Id.coverPage, 'Cover page', 'Report option');
    register.add(Id.infoPage, 'Info page', 'Report option');
    register.add(Id.globalSpecifications, 'Global specifications', 'Report option');
    register.add(Id.itemSummary, 'Item summary', 'Report option');
    register.add(Id.quoteCalculations, "{{glossary 'quote'}} calculations", 'Report option');
    register.add(Id.itemDetails, 'Item details', 'Report option');
    register.add(Id.annotations, 'Annotations & notes', 'Report option');
    register.add(Id.dealerTerms, 'Dealer terms & conditions', 'Report option');
    register.add(Id.supplierTerms, "{{glossary 'supplier'}} terms & conditions", 'Report option');
    register.add(Id.lineItemPrice, 'Line item price', 'Report option');
    register.add(Id.groupTotals, 'Group totals', 'Report option');
    register.add(Id.priceSummary, 'Price summary', 'Report option');
    register.add(Id.priceSummaryNone, 'None', 'Price summary choice: no totals');
    register.add(Id.priceSummarySummary, 'Summary', 'Price summary choice: the totals summary');
    register.add(Id.priceSummaryDealerCost, 'Dealer cost', 'Price summary choice: dealer cost');
    register.add(Id.itemSummaryLayout, 'Layout', 'Report option: how each item prints in the item summary');
    register.add(Id.itemSummaryLayoutCompact, 'Compact', 'Item summary layout choice: one short row per item');
    register.add(
      Id.itemSummaryLayoutDetailed,
      'Detailed',
      'Item summary layout choice: taller rows with a larger thumbnail and wider options'
    );
    register.add(Id.infoPageLayout, 'Layout', 'Report option: how the info page sets out the quote details');
    register.add(Id.infoPageLayoutStandard, 'Standard', 'Info page layout choice: details beside the address and map');
    register.add(Id.infoPageLayoutGrid, 'Grid', 'Info page layout choice: details in rows with the map below');
    register.add(Id.map, 'Map', 'Report option: the map of the address on the info page');
    register.add(Id.sectionFlow, 'Section flow', 'Report option: whether each section starts a new page');
    register.add(Id.sectionFlowPageBreak, 'Page break', 'Section flow choice: each section starts a new page');
    register.add(
      Id.sectionFlowContinuous,
      'Continuous',
      'Section flow choice: each section follows straight after the one before'
    );
    register.add(Id.moveUp, 'Move {{section}} up', 'Button that moves a report section one place earlier');
    register.add(Id.moveDown, 'Move {{section}} down', 'Button that moves a report section one place later');
    register.add(Id.dragToReorder, 'Drag to reorder', 'Tooltip on the handle of a report section');
    register.add(Id.resetOrder, 'Reset order', 'Button that puts the report sections back in their default order');
    register.add(Id.pages, 'Pages', 'Heading of the sketch of the pages a report prints, in order');
    register.add(Id.dimensions, 'Dimensions', 'Report option');
    register.add(Id.manufacturerNotes, 'Manufacturer notes', 'Report option');
    register.add(Id.initialsPanel, 'Initials panel', 'Report option');
  }
}
